import React, { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { Box, Grid, TextField } from "@material-ui/core";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@material-ui/icons";
import DragHandleIcon from "@material-ui/icons/DragHandle";
import { PaginationIconButton } from "../../../../util";
import { useRowSelect, useTable, useGlobalFilter } from "react-table";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";

const CustomCheckBox = ({ index, selectedRows, setSelectedRows, disabled }) => {
  const [filled, setFilled] = useState(false);
  return (
    <button
      style={{
        width: "25px",
        height: "25px",
        borderRadius: "4px",
        border: "1px solid #CCCCCC",
        background: filled ? "#CCCCCC" : "#FFF",
        cursor: "pointer",
      }}
      type="button"
      disabled={disabled}
      onClick={() => {
        if (!filled) setSelectedRows([...selectedRows, index]);
        else setSelectedRows(selectedRows.filter((value) => value !== index));
        setFilled(!filled);
      }}
    ></button>
  );
};

const IsDraggingContext = React.createContext(false);

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

class DraggableTableCell extends React.Component {
  getSnapshotBeforeUpdate(prevProps) {
    if (!this.ref) {
      return null;
    }

    const isDragStarting =
      this.props.isDragOccurring && !prevProps.isDragOccurring;

    if (!isDragStarting) {
      return null;
    }

    const { width, height } = this.ref.getBoundingClientRect();

    const snapshot = {
      width,
      height,
    };

    return snapshot;
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const ref = this.ref;
    if (!ref) {
      return;
    }

    if (snapshot) {
      if (ref.style.width === snapshot.width) {
        return;
      }
      ref.style.width = `${snapshot.width}px`;
      ref.style.height = `${snapshot.height}px`;
      return;
    }

    if (this.props.isDragOccurring) {
      return;
    }

    // inline styles not applied
    if (ref.style.width == null) {
      return;
    }

    // no snapshot and drag is finished - clear the inline styles
    ref.style.removeProperty("height");
    ref.style.removeProperty("width");
  }

  setRef = (ref) => {
    this.ref = ref;
  };

  render() {
    return (
      <TableCell
        style={{
          width: 500,
          borderBottom: "none",
          padding: 0,
          paddingLeft: "20px",
          paddingBottom: "10px",
          color: this.props.selectedRows.includes(this.props.row.index)
            ? "#CCCCCC"
            : "#000",
        }}
        ref={this.setRef}
        align={this.props.align}
      >
        {this.props.children}
      </TableCell>
    );
  }
}

const DraggableTableRow = ({
  snapshot,
  data,
  row,
  provided,
  className,
  text,
  selectedRows,
  setSelectedRows,
  disabled,
}) => {
  return (
    <IsDraggingContext.Consumer>
      {(isDragging) => (
        <TableRow
          className={className}
          ref={provided.innerRef}
          isDragging={snapshot.isDragging}
          {...provided.draggableProps}
        >
          {row.original.selected &&
            row.cells.map((cell) =>
              cell.column.id === "checkbox" ? (
                <TableCell
                  {...cell.getCellProps()}
                  style={{
                    width: 10,
                    borderBottom: "none",
                    padding: 0,
                    paddingLeft: "20px",
                    paddingBottom: "10px",
                  }}
                >
                  <CustomCheckBox
                    index={row.index}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    disabled={disabled}
                  />
                </TableCell>
              ) : (
                <DraggableTableCell
                  isDragOccurring={isDragging}
                  align="left"
                  scope="row"
                  {...cell.getCellProps()}
                  selectedRows={selectedRows}
                  row={row}
                >
                  {cell.render("Cell")}
                </DraggableTableCell>
              )
            )}
          <TableCell
            style={{
              width: 10,
              borderBottom: "none",
              padding: 0,
              paddingRight: "20px",
              paddingBottom: "10px",
              color: "#CCCCCC",
              display: row.original.selected ? "" : "none",
            }}
            {...provided.dragHandleProps}
          >
            <DragHandleIcon />
          </TableCell>
        </TableRow>
      )}
    </IsDraggingContext.Consumer>
  );
};

export default function PostSection(props) {
  const {
    setSelectedNews,
    selecteddata,
    whatsnewsdata,
    classes,
    disabled,
  } = props;

  const notSelectedPosts = whatsnewsdata
    .filter(
      (item) =>
        item && item.status === "PUBLISHED" && !selecteddata.includes(item.id)
    )
    .map((item) => ({
      id: item.id,
      title: item.title,
      selected: false,
    }));

  const selectedPosts = selecteddata.map((item) => ({
    id: item,
    title: whatsnewsdata.find((data) => data.id === item)
      ? whatsnewsdata.find((data) => data.id === item).title
      : "",
    selected: true,
  }));

  const [selectedRows, setSelectedRows] = useState([]);
  const [isProcessing, setIsProcessing] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [data, setData] = useState([...notSelectedPosts, ...selectedPosts]);

  const columns = React.useMemo(
    () => [
      {
        Header: "allPosts",
        accessor: "title",
      },
    ],
    []
  );

  const onBeforeDragStart = () => {
    setIsDragging(true);
  };

  //callback passed to dragdrop context
  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }
    setIsLoading(true);
    const items = reorder(data, result.source.index, result.destination.index);

    setSelectedNews(
      items.filter((item) => item.selected).map((item) => item.id)
    );
    setData(items);
  };

  useEffect(() => {
    if (isProcessing) setIsProcessing(false);
    setSelectedNews(
      data.filter((item) => item.selected).map((item) => item.id)
    );
  }, [data]);

  useEffect(() => {
    if (isProcessing) setIsLoading(true);
    else setIsLoading(false);
    if (isLoading) setIsProcessing(false);
  }, [isProcessing, isLoading]);

  const calculateSelectedPosts = () => {
    return data.filter((post) => post.selected).length;
  };

  const {
    getTableProps,
    getTableBodyProps,
    rows,
    prepareRow,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
    },
    useGlobalFilter,
    useRowSelect,
    (hook) => {
      hook.visibleColumns.push((columns) => [
        {
          id: "checkbox",
          Header: () => <></>,
          disableGlobalFilter: true,
        },
        ...columns,
      ]);
    }
  );

  const getUpdatedData = (postIds) => {
    const newData = data;
    postIds.forEach(id => {
        const indexToUpdate = data.findIndex((obj) => obj.id === id);
        newData[indexToUpdate].selected = !newData[indexToUpdate].selected;
    });
    return newData;
  };

  const handleSelect = () => {
    const filteredRows = rows.filter(
      (row) => !row.original.selected && selectedRows.includes(row.index)
    );
    setSelectedRows([]);
    const selectedIds = filteredRows.map((row) => row.original.id);
    setIsProcessing(true);
    const newData = getUpdatedData(selectedIds);
    setSelectedNews(
      newData.filter((item) => item.selected).map((item) => item.id)
    );
    setData(newData);
  };

  const handleUnselect = () => {
    const filteredRows = rows.filter(
      (row) => row.original.selected && selectedRows.includes(row.index)
    );
    setSelectedRows([]);
    const selectedIds = filteredRows.map((row) => row.original.id);
    setIsProcessing(true);
    const newData = getUpdatedData(selectedIds);
    setSelectedNews(
      newData.filter((item) => item.selected).map((item) => item.id)
    );
    setData(newData);
  };

  return (
    <Box
      className={classes.innerBox}
      style={{ paddingBottom: 0, marginBottom: 0 }}
    >
      <TextField
        label="Search"
        variant="outlined"
        margin="dense"
        className={classes.textInput}
        fullWidth
        onChange={(e) => {
          setGlobalFilter(e.target.value);
        }}
      />
      <Box ml={1}>
        <Grid container xs={12} alignItems="center" className={classes.posts}>
          <Box style={{ width: "47.5%", height: "405px", overflowY: "auto" }}>
            <h4 style={{ margin: "20px", marginBottom: "10px" }}>
              <small>All Posts</small>
            </h4>
            {isLoading && isProcessing ? (
              <p>Loading...</p>
            ) : (
              <Table
                aria-label="simple table"
                component="ul"
                {...getTableProps()}
                style={{
                  minWidth: 0,
                }}
              >
                <TableBody {...getTableBodyProps()}>
                  {rows.map((row, i) => {
                    prepareRow(row);
                    return (
                      <TableRow {...row.getRowProps()}>
                        {!row.original.selected &&
                          row.cells.map((cell) =>
                            cell.column.id === "checkbox" ? (
                              <TableCell
                                {...cell.getCellProps()}
                                style={{
                                  width: 10,
                                  borderBottom: "none",
                                  padding: 0,
                                  paddingLeft: "20px",
                                  paddingBottom: "10px",
                                }}
                              >
                                <CustomCheckBox
                                  index={row.index}
                                  selectedRows={selectedRows}
                                  setSelectedRows={setSelectedRows}
                                  disabled={disabled}
                                />
                              </TableCell>
                            ) : (
                              <TableCell
                                {...cell.getCellProps()}
                                style={{
                                  width: 500,
                                  borderBottom: "none",
                                  padding: 0,
                                  paddingLeft: "20px",
                                  paddingBottom: "10px",
                                  color: selectedRows.includes(row.index)
                                    ? "#CCCCCC"
                                    : "#000",
                                }}
                              >
                                {cell.render("Cell")}
                              </TableCell>
                            )
                          )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </Box>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            className={classes.postsControl}
          >
            <PaginationIconButton
              style={{ marginBottom: "5px" }}
              onClick={handleSelect}
              disabled={disabled}
            >
              <KeyboardArrowRight />
            </PaginationIconButton>
            <PaginationIconButton onClick={handleUnselect} disabled={disabled}>
              <KeyboardArrowLeft />
            </PaginationIconButton>
          </Box>
          <Box style={{ width: "47.5%", height: "405px", overflowY: "auto" }}>
            <h4 style={{ margin: "20px", marginBottom: "10px" }}>
              <small>Selected Posts ({calculateSelectedPosts()})</small>
            </h4>
            {isLoading && isProcessing ? (
              <p>Loading...</p>
            ) : (
              <IsDraggingContext.Provider value={isDragging}>
                <Table
                  aria-label="simple table"
                  component="ul"
                  {...getTableProps()}
                  style={{
                    minWidth: 0,
                  }}
                >
                  <DragDropContext
                    onBeforeDragStart={onBeforeDragStart}
                    onDragEnd={onDragEnd}
                  >
                    <Droppable droppableId="droppable" type="QUESTIONS">
                      {(provided, snapshot) => (
                        <TableBody
                          component="div"
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          {...getTableBodyProps()}
                        >
                          {rows.map((row, i) => {
                            prepareRow(row);
                            return (
                              <Draggable
                                key={row.id}
                                draggableId={row.id}
                                index={i}
                                isDragDisabled={disabled}
                              >
                                {(provided, snapshot) => (
                                  <DraggableTableRow
                                    key={row.id}
                                    data={row.original}
                                    row={row}
                                    provided={provided}
                                    snapshot={snapshot}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                    disabled={disabled}
                                  />
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </TableBody>
                      )}
                    </Droppable>
                  </DragDropContext>
                </Table>
              </IsDraggingContext.Provider>
            )}
          </Box>
        </Grid>
      </Box>
    </Box>
  );
}
